import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { monitorCampaignGeneration, formatCampaignStatus } from '../../services/websocket';
import type { CampaignMediaData } from '../../services/websocket';

import { triggerContentGenerationRequest } from '../../redux/slices/contentGenerationSlice';
import type { RootState } from '../../redux/store';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';

interface ProgressStep {
  status: string;
  message: string;
  timestamp: string;
}

const CampaignGenerator: React.FC = () => {
  const location = useLocation();
  const { navigate } = useRestaurantNav();
  const dispatch = useDispatch();
  const { ideaId, ideaType, taskId, campaignId } = location.state || {};
  const restaurantId = "4bebbb8a-a2bf-4d53-b287-842c3c5be847";
  // const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  
  const [currentData, setCurrentData] = React.useState<CampaignMediaData | null>(null);
  const [progressSteps, setProgressSteps] = React.useState<ProgressStep[]>([]);
  const [isCompleted, setIsCompleted] = React.useState(false);
  const [isFetchingCampaign, setIsFetchingCampaign] = React.useState(false);

  React.useEffect(() => {
    // Unified monitoring for both taskId and ideaId
    const monitorId = taskId || "";
    if (!monitorId || !restaurantId) {
      console.error('[Campaign Generator] Missing required parameters');
      return;
    }
    
    console.log('[Campaign Generator] Starting monitoring:', taskId ? `taskId: ${taskId}` : `ideaId: ${ideaId}`);
    
    const cleanup = monitorCampaignGeneration(
      restaurantId,
      monitorId,
      ideaType,
      (event) => {
        console.log('📡 Monitor event:', event);
        
        switch (event.type) {
          case 'progress':
            if (event.data) {
              setCurrentData(event.data);
              
              setProgressSteps(prev => {
                const exists = prev.some(step => 
                  step.status === event.data!.stage && 
                  step.message === event.data!.message
                );
                
                if (!exists) {
                  return [...prev, {
                    status: event.data!.stage,
                    message: event.data!.message,
                    timestamp: event.data!.createdAt
                  }];
                }
                return prev;
              });
              
              if (event.data.lifecycle === 'Completed' && event.data.percentCompleted >= 100) {
                setIsCompleted(true);
              }
            }
            break;
          
          case 'done':
            if (event.data) {
              setCurrentData(event.data);
              setIsCompleted(true);
            }
            break;
        }
      },
      (error) => {
        console.error('❌ Monitor error:', error);
      }
    );

    return cleanup;
  }, [taskId, restaurantId, ideaId, ideaType]);

  // Fetch campaign details when monitoring completes
  React.useEffect(() => {
    const fetchCampaign = async () => {
      if (!isCompleted || !campaignId || !restaurantId || isFetchingCampaign) return;
      
      setIsFetchingCampaign(true);
      console.log('[Campaign Generator] Fetching campaign details:', campaignId);
      
      try {
        const response = await fetch(
          `${import.meta.env.VITE_PROMOFLOW_V2_API_URL}/campaign/${campaignId}`,
          {
            headers: {
              'restaurant-id': restaurantId,
            },
          }
        );
        
        if (!response.ok) throw new Error('Failed to fetch campaign');
        
        const result = await response.json();
        console.log('[Campaign Generator] Campaign data fetched:', result);
        
        // Navigate to flyer with campaign data and query params
        if (result.success && result.data) {
          navigate(`/${restaurantId}/flyer?campaignId=${result.data.campaign.id}&campaignType=${result.data.campaign.type}`, {
            state: {
              campaignId: result.data.campaign.id,
              campaignType: result.data.campaign.type,
              campaignData: result.data,
            }
          });
        }
      } catch (error) {
        console.error('[Campaign Generator] Error fetching campaign:', error);
        // Fallback navigation
        navigate(`/${restaurantId}/campaigns`);
      }
    };
    
    fetchCampaign();
  }, [isCompleted, campaignId, restaurantId, isFetchingCampaign, navigate]);

  const displayProgress = Math.max(10, currentData?.percentCompleted ?? 10);
  const showLoadingOverlay = isCompleted && isFetchingCampaign;

  return (
    <>
      <style>{`
          @keyframes shine {
            0% {
              transform: translateX(-100%) rotate(-90deg);
            }
            100% {
              transform: translateX(100%) rotate(-90deg);
            }
          }
          
          @keyframes orbit {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes counter-orbit {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
          }
          
          @keyframes flicker {
            0% { opacity: 0.8; }
            50% { opacity: 1; }
            100% { opacity: 0.8; }
          }
          
          @keyframes hub-pulse {
            0% { transform: scale(1); filter: drop-shadow(0 0 20px rgba(255, 127, 80, 0.6)); }
            50% { transform: scale(1.05); filter: drop-shadow(0 0 35px #FF7F50); }
            100% { transform: scale(1); filter: drop-shadow(0 0 20px rgba(255, 127, 80, 0.6)); }
          }
          
          @keyframes float-monitor {
            0% { transform: translateY(0); }
            100% { transform: translateY(-5px); }
          }
          
          @keyframes sparkle-burst {
            0% { transform: scale(0); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: scale(0); opacity: 0; }
          }
          
          @keyframes orbit-float {
            0% { 
              transform: translate(-50%, -50%) rotate(0deg) translateX(0px) rotate(0deg);
              opacity: 0;
              scale: 0.5;
            }
            10% { 
              opacity: 1;
              scale: 1;
            }
            90% { 
              opacity: 1;
              scale: 1;
            }
            100% { 
              transform: translate(-50%, -50%) rotate(360deg) translateX(120px) rotate(-360deg);
              opacity: 0;
              scale: 0.5;
            }
          }
      `}</style>

      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-6 relative">
        {/* Loading Overlay when generating content */}
        {showLoadingOverlay && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-[#212121] rounded-xl p-8 border border-gray-700 shadow-2xl text-center max-w-md">
              <div className="mb-4">
                <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
              <h3 className="text-white text-xl font-bold mb-2">Generating Your Campaign</h3>
              <p className="text-gray-400 text-sm">
                Creating flyers and content for your campaign...
              </p>
            </div>
          </div>
        )}

        <div className="w-full max-w-5xl">
          <div className="rounded-xl p-8 bg-[#212121] border border-gray-700 shadow-lg">
            <div className="grid grid-cols-12 gap-8">
              {/* Left Side: Progress */}
              <div className="col-span-4 flex flex-col items-center justify-center px-6 overflow-visible">
                <div className="relative w-full overflow-visible">
                  {/* Development Scene */}
                  <div className="relative h-50 mb-6 flex items-center justify-center">
                    {/* Social Media & File Icons - Orbiting */}
                    <div className="absolute w-full h-full pointer-events-none">
                      {[
                        { 
                          name: 'facebook', 
                          color: '#1877F2',
                          duration: '6s',
                          delay: '0s',
                          path: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z'
                        },
                        { 
                          name: 'instagram', 
                          color: '#E4405F',
                          duration: '6s',
                          delay: '1s',
                          path: 'M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z M17.5 6.5h.01 M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2z'
                        },
                        { 
                          name: 'tiktok', 
                          color: '#000000',
                          duration: '6s',
                          delay: '2s',
                          path: 'M9 12a4 4 0 1 0 4 4V4s1 2 4 2'
                        },
                        { 
                          name: 'whatsapp', 
                          color: '#25D366',
                          duration: '6s',
                          delay: '3s',
                          path: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'
                        },
                        { 
                          name: 'image', 
                          color: '#9C27B0',
                          duration: '6s',
                          delay: '4s',
                          path: 'M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'
                        },
                        { 
                          name: 'yelp', 
                          color: '#D32323',
                          duration: '6s',
                          delay: '5s',
                          path: 'M12 2l-3 7-7 2 7 3-1 8 4-5 4 5-1-8 7-3-7-2z'
                        },
                        { 
                          name: 'file', 
                          color: '#FF9800',
                          duration: '6s',
                          delay: '0.5s',
                          path: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z'
                        },
                      ].map((icon, index) => (
                        <div
                          key={icon.name}
                          className="absolute w-10 h-10 bg-white/80 rounded-full shadow-lg flex items-center justify-center backdrop-blur-sm"
                          style={{
                            top: '50%',
                            left: '50%',
                            animation: `orbit-float ${icon.duration} ease-in-out infinite ${icon.delay}`,
                            border: '2px solid rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="20" height="20" fill={icon.color} stroke={icon.name === 'tiktok' || icon.name === 'whatsapp' ? icon.color : 'none'} strokeWidth={icon.name === 'tiktok' || icon.name === 'whatsapp' ? 2 : 0} strokeLinecap="round" strokeLinejoin="round">
                            <path d={icon.path} />
                          </svg>
                        </div>
                      ))}
                    </div>

                    {/* Single Monitor with Phone in Center */}
                    <div className="relative w-full h-48 flex items-end justify-center">  
                      {/* Main Monitor */}
                      <div className="relative" style={{ animation: 'float-monitor 3s ease-in-out infinite 0s' }}>
                        <div className={`w-40 h-30 bg-gradient-to-br from-orange-600 to-red-600 rounded-t-xl border-4 border-orange-400 shadow-2xl overflow-hidden relative`}>
                          {/* Code Lines Animation in Monitor */}
                          <div className="p-3 space-y-1.5">
                            {[...Array(10)].map((_, i) => (
                              <div 
                                key={i} 
                                className="h-1.5 bg-white/60 rounded"
                                style={{ 
                                  width: `${Math.random() * 50 + 30}%`,
                                  animation: `flicker 1.5s infinite ${i * 0.2}s`
                                }}
                              />
                            ))}
                          </div>
                        </div>
                        <div className={`w-40 h-1.5 bg-gray-600 mx-auto`}></div>
                        <div className={`w-30 h-3 bg-gray-700 mx-auto rounded-b-lg`}></div>
                      </div>
                    </div>

                    {/* Decorative Circles */}
                    {[
                      { size: 'w-2 h-2', color: 'bg-orange-400', top: '20%', left: '0%', delay: '0s' },
                      { size: 'w-3 h-3', color: 'bg-yellow-300', top: '60%', right: '5%', delay: '1s' },
                      { size: 'w-2 h-2', color: 'bg-orange-300', bottom: '25%', left: '10%', delay: '1.5s' },
                    ].map((circle, i) => (
                      <div
                        key={i}
                        className={`absolute ${circle.size} ${circle.color} rounded-full opacity-60`}
                        style={{
                          top: circle.top,
                          bottom: circle.bottom,
                          left: circle.left,
                          right: circle.right,
                          animation: `flicker 3s infinite ${circle.delay}`
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Progress Percentage Circle */}
                  <div className="text-center">
                    {/* Horizontal Progress Bar */}
                    <div className={`w-full h-2 bg-gray-700 rounded-full overflow-hidden`}>
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500 relative"
                        style={{ width: `${displayProgress}%` }}
                      >
                        {/* Shine effect */}
                        {!isCompleted && (
                          <div 
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                            style={{ animation: 'shine 2s infinite' }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="col-span-1 flex justify-center">
                      <div className="w-px h-full bg-gradient-to-b from-orange-500 via-red-500 to-purple-500"></div>
                    </div>

                    <div className={`text-sm font-medium italic text-gray-300  mt-2`}>
                      {Math.round(displayProgress)}%
                    </div>
                   
                  </div>
                </div>
              </div>

              {/* Divider */}
              

              {/* Right Side: Steps */}
              <div className="col-span-7">
                <div className="space-y-4">
                  {progressSteps.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <div className="animate-pulse">Initializing campaign generation...</div>
                    </div>
                  ) : (
                    progressSteps.map((step, index) => {
                      const isCurrentStep = index === progressSteps.length - 1 && !isCompleted;
                      const isCompletedStep = index < progressSteps.length - 1 || isCompleted;
                      
                      return (
                        <div 
                          key={`${step.status}-${index}`}
                          className={`flex items-start gap-4 p-4 rounded-lg transition-all ${
                            isCurrentStep 
                              ? 'bg-orange-500/20' 
                              : isCompletedStep
                              ? 'bg-gray-800/50'
                              : ''
                          }`}
                        >
                          <div className="flex-shrink-0 mt-1">
                            {isCompletedStep ? (
                              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : isCurrentStep ? (
                              <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                                <div className="w-3 h-3 bg-white rounded-full animate-ping"></div>
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-700"></div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <h4 className={`text-sm font-bold mb-1 ${
                              isCurrentStep 
                                ? 'text-orange-400' 
                                : isCompletedStep
                                ? 'text-green-400'
                                : 'text-white'
                            }`}>
                              {formatCampaignStatus(step.status)}
                            </h4>
                            <p className="text-xs text-gray-400 leading-relaxed">
                              {step.message}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignGenerator;
